import CopyToClipboard from '../../copyToClipboard';
import { Mail, PhoneIcon, ChevronRight, CheckIcon } from 'assets/icons';
import BadgeSquare from '../../badge/badgeSquare';
import Tooltip from '../../tooltips/Tooltips';
import UseContext from '../../../hooks/useContext';
import { capitalizeFirst } from '../../../helper/helper';
import {
  countLicenceProgress,
  fillTemplate,
  formatDay,
  getLicenceLabel,
  getLicenceStatusBadge,
} from '../../../helper/licenceValidation';

/** One licence or CACES of the expanded card: label, sending date, state. */
const LicenceRow = ({ entry, language, onVerify }) => {
  const label = getLicenceLabel(entry, language);
  const sentOn = formatDay(entry?.updated);
  const badge = getLicenceStatusBadge(entry?.validation, language);
  const verify = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onVerify?.(entry);
  };

  return (
    <div
      className="py-2 px-3 flex items-center gap-3 rounded hover:bg-gray-100 transition-colors cursor-pointer"
      onClick={verify}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') verify(event);
      }}
      role="button"
      tabIndex={0}
      aria-label={fillTemplate(
        language?.getValue('licenceValidation.verify', 'Vérifier {licence}'),
        { licence: label },
      )}
    >
      <div className="flex items-center gap-1 min-w-0 flex-shrink text-left">
        <span className="text-sm text-gray-900 truncate">{label}</span>
        {sentOn && (
          <span className="text-sm text-gray-500 whitespace-nowrap">
            ({sentOn})
          </span>
        )}
      </div>
      <div className="flex-1" />
      <div className="max-w-[120px] truncate">
        <BadgeSquare type={badge.type} customLabel={badge.label} />
      </div>
      <div
        aria-hidden="true"
        className="hidden tab:flex hover:bg-gray-200 transition-colors rounded p-1 w-8 h-8 items-center justify-center"
      >
        <CheckIcon w={24} h={12} color="#111827" />
      </div>
    </div>
  );
};

/**
 * A temp worker of the licence and CACES pending list (GET
 * admin/licences/pending), built like IdentityValidationMobileCard: the
 * summary gives the contact details and the decided over total entries, the
 * expanded part one row per entry.
 */
const LicenceValidationMobileCard = ({
  tempWorker,
  onOpenTempWorker,
  onVerifyLicence,
}) => {
  const { language } = UseContext();
  const fullName =
    `${capitalizeFirst(tempWorker?.firstname)} ${capitalizeFirst(tempWorker?.lastname)}`.trim() ||
    'N/A';
  const email = tempWorker?.email || '';
  const phone = tempWorker?.phone || '';
  const licences = Array.isArray(tempWorker?.licences)
    ? tempWorker.licences
    : [];
  const { handled, total } = countLicenceProgress(licences);

  return (
    <details className="bg-white rounded-md border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-all duration-200 group overflow-visible motion-safe:[&[open]>div]:animate-expandIn">
      <summary className="list-none cursor-pointer">
        <div className="p-4 pb-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 flex-1 min-w-0">
              <Tooltip
                text={language?.getValue(
                  'licenceValidation.openTempWorker',
                  'Voir la fiche',
                )}
                placement="top"
              >
                <h3
                  className="font-semibold text-gray-900 text-base truncate cursor-pointer hover:underline decoration-dashed transition-colors max-w-[200px] md:max-w-[250px] flex-shrink-0"
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    onOpenTempWorker?.(tempWorker);
                  }}
                  title={fullName}
                >
                  {fullName}
                </h3>
              </Tooltip>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                {email && (
                  <div
                    className="flex items-center gap-1.5"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Mail wh={16} color="#6B7280" />
                    <CopyToClipboard text={email}>
                      <span className="text-gray-600 truncate max-w-[180px] md:max-w-[250px]">
                        {email}
                      </span>
                    </CopyToClipboard>
                  </div>
                )}
                {phone && (
                  <div
                    className="flex items-center gap-1.5"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <PhoneIcon wh={16} color="#6B7280" />
                    <CopyToClipboard text={phone}>
                      <span className="text-gray-600">{phone}</span>
                    </CopyToClipboard>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <BadgeSquare
                type={9}
                bordered
                customLabel={`${handled}/${total}`}
              />
              <div className="group-open:rotate-90 transition-transform flex-shrink-0">
                <ChevronRight wh={20} color="#4B5563" />
              </div>
            </div>
          </div>
        </div>
      </summary>

      <div className="px-4 pb-4 border-t border-gray-100 pt-3">
        {licences.map((entry, index) => (
          <LicenceRow
            key={entry?._id || `${entry?.licence}-${index}`}
            entry={entry}
            language={language}
            onVerify={onVerifyLicence}
          />
        ))}
      </div>
    </details>
  );
};

export default LicenceValidationMobileCard;
